import type {MenuItemConfig} from '@myparcel-frontend/ui';
import {Cog24} from '@myparcel-frontend/icons/legacy';
import {markRaw} from 'vue';

export const getShopSettingsMenu = ({
  shopId,
  enableFulfilment = false,
  enableSalesChannels = false,
  enableEmailCenter = false,
  enableSalesChannelsFeature = false,
  isFulfilmentActive = false,
  enableGeneralSettings = false,
  enableIntegrations = false,
  isOrderRulesAccessible = false,
  enableReturn = false,
}: {
  shopId: string;
  enableFulfilment: boolean;
  enableSalesChannels: boolean;
  enableEmailCenter: boolean;
  enableSalesChannelsFeature: boolean;
  isFulfilmentActive: boolean;
  enableGeneralSettings: boolean;
  enableIntegrations: boolean;
  isOrderRulesAccessible: boolean;
  enableReturn: boolean;
}): MenuItemConfig => {
  const menuItem: MenuItemConfig = {
    type: 'parent',
    label: 'settings.shop.title',
    multiplyLabel: true,
    icon: markRaw(Cog24),
    children: [
      {
        type: 'internal',
        label: 'general',
        to: {
          name: 'shop-settings-general',
          params: {
            shopId,
          },
        },
        show: () => enableGeneralSettings,
      },
      {
        type: 'internal',
        to: {
          name: 'shop-settings.branding',
          params: {
            shopId,
          },
        },
        label: 'settings.shop.branding.title',
      },
      {
        type: 'internal',
        to: {
          name: 'shop-settings.emails',
          params: {
            shopId,
          },
        },
        activeRouteNameAliases: ['shop-settings-emails-flows', 'shop-settings-emails-settings'],
        label: 'shipment.menu.items.emails',
        show: () => enableEmailCenter,
      },
    ],
  };

  if (enableFulfilment) {
    menuItem.children.push({
      type: 'internal',
      to: {
        name: 'shop-settings-orders',
        params: {
          shopId,
        },
      },
      label: 'subscriptions.myOrders.banner',
      show: () => isFulfilmentActive,
    });
  }

  menuItem.children.push({
    type: 'internal',
    to: {
      name: 'shop-settings-rules-orders',
      params: {
        shopId,
      },
    },
    label: 'rules.rule',
    multiplyLabel: true,
    show: () => isOrderRulesAccessible,
    activeRouteNameAliases: [
      'shop-settings-rules-returns',
      'shop-settings-rules-returns-create',
      'shop-settings-rules-returns-edit',
      'shop-settings-rules-shipments',
      'shop-settings-rules-shipments-create',
      'shop-settings-rules-shipments-edit',
    ],
  });

  menuItem.children.push(
    {
      type: 'internal',
      to: {
        name: 'shop-settings-labels',
        params: {
          shopId,
        },
      },
      label: 'shipment.index.shipment',
      multiplyLabel: true,
      activeRouteNameAliases: [
        'shop-settings-shipping-rules',
        'shop-settings-shipping-rules-create',
        'shop-settings-shipping-rules-edit',
        'shop-settings-carriers',
        'dimensions-presets',
        'shop-settings-dimensions-presets',
        'shop-settings-dimensions-presets-create',
        'shop-settings-dimensions-presets-edit',
      ],
    },
    {
      type: 'internal',
      to: {
        name: 'shop-settings-portal',
        params: {
          shopId,
        },
      },
      label: 'settings.shop.portal.title',
    },
    {
      type: 'internal',
      activeRouteNameAliases: [
        'shop-settings-returns-portal',
        'shop-settings-returns-rules',
        'shop-settings-returns-rules-create',
        'shop-settings-returns-rules-edit',
      ],
      to: {
        name: 'shop-settings-returns-policy',
        params: {
          shopId,
        },
      },
      label: 'shipping.return',
      multiplyLabel: true,
      show: () => enableReturn,
    },
    {
      type: 'internal',
      to: {
        name: 'shop-settings-integration',
        params: {
          shopId,
        },
      },
      label: 'integrations.title',
      multiplyLabel: true,
      show: () => enableIntegrations,
    },
    {
      type: 'internal',
      to: {
        name: 'shop-settings-company-billing',
        params: {
          shopId,
        },
      },
      activeRouteNameAliases: [
        'shop-settings-company-return',
        'shop-settings-company-export',
        'shop-settings-company-webshop',
        'shop-settings-company-bank',
      ],
      label: 'settings.shop.company.title',
    },
  );

  if (enableSalesChannels) {
    menuItem.children.push({
      type: 'internal',
      to: {
        name: 'sales-channels',
        params: {
          shopId,
        },
      },
      label: 'integrations.salesChannels.title',
      show: () => isFulfilmentActive || enableSalesChannelsFeature,
    });
  }

  return menuItem;
};
